import LogRocket from 'logrocket';

// LogRocket session replay — frontend instrumentation.
//
// Why this wrapper exists:
// LogRocket.init() can technically be called multiple times but the
// docs are clear that's wasteful. LogRocket.identify() requires init
// to have happened. Components scattered across the app (Record,
// ThankYou, Dashboard, AdminLogin) all want to "tell LogRocket who
// this person is once you know" without each one having to coordinate
// init order with the rest. This module owns both.
//
// Cost-management notes:
// LogRocket bills per-session. We gate init to import.meta.env.PROD
// so local Vite dev (`npm run dev`) doesn't create sessions. There's
// no Cloudflare-side dev environment for this project right now, so
// in practice PROD === always for deployed code -- the gate is purely
// to keep dev local quiet. If we add a staging env later, add a
// VITE_LOGROCKET_ENABLE flag here.
//
// Privacy notes:
// LogRocket by default records DOM mutations, network metadata, and
// XHR/fetch payloads. The customer flow includes PII (email, name,
// address) that's fine to record for session replay debugging --
// LogRocket itself is SOC 2 and we have a DPA with them. If we ever
// need to mask a field, add data-private="true" to the element or
// use LogRocket.getSessionURL() + a request-redaction hook.

const APP_ID = 'uyjcld/video-testimonials-submission';

let initialized = false;
let identified = false;

export function initLogRocket() {
  if (initialized) return;
  if (!import.meta.env.PROD) return;     // skip in `npm run dev`
  try {
    LogRocket.init(APP_ID);
    initialized = true;
  } catch (e) {
    // Network blocked, ad-blocker, etc. Don't let LogRocket failures
    // break the app — it's an observability tool, not load-bearing.
    console.warn('LogRocket init failed (non-fatal):', e?.message || e);
  }
}

// Tag the current session with the user's identity. Safe to call
// before init — we'll deferred-init if needed and apply identity
// after. Idempotent: re-calling with the same email is a no-op.
// Use `role` to distinguish customer vs admin in the LogRocket UI.
export function identifyUser({ email, name, role }) {
  if (!email) return;
  if (!import.meta.env.PROD) return;
  // Ensure init has run. Calling twice is harmless thanks to the
  // initialized flag above.
  initLogRocket();
  if (!initialized) return;              // init refused (blocked, etc.)
  if (identified) return;                // idempotent on first email
  try {
    LogRocket.identify(email, {
      email,
      ...(name ? { name } : {}),
      ...(role ? { role } : {}),
    });
    identified = true;
  } catch (e) {
    console.warn('LogRocket identify failed (non-fatal):', e?.message || e);
  }
}
